/**
 * Pure data layer for shape state management.
 * Follows the same registry + diff pattern as marker-state.ts.
 */

import type { TakShape } from '@vtak/shared';

/** Diff result between two shape sets */
export interface ShapeDiff {
  added: TakShape[];
  removed: string[];
  updated: TakShape[];
}

/** Registry of active shapes keyed by UID */
const shapeRegistry = new Map<string, TakShape>();

/** Get all current shapes */
export function getAllShapes(): ReadonlyMap<string, TakShape> {
  return shapeRegistry;
}

/** Compute diff between current registry and incoming shapes */
export function computeShapeDiff(incoming: TakShape[]): ShapeDiff {
  const incomingMap = new Map(incoming.map((s) => [s.uid, s]));
  const added: TakShape[] = [];
  const updated: TakShape[] = [];
  const removed: string[] = [];

  for (const [uid, shape] of incomingMap) {
    const existing = shapeRegistry.get(uid);
    if (!existing) {
      added.push(shape);
    } else if (hasChanged(existing, shape)) {
      updated.push(shape);
    }
  }

  for (const uid of shapeRegistry.keys()) {
    if (!incomingMap.has(uid)) {
      removed.push(uid);
    }
  }

  return { added, removed, updated };
}

/** Apply a diff to the shape registry */
export function applyShapeDiffToRegistry(diff: ShapeDiff, incoming: TakShape[]): void {
  for (const uid of diff.removed) {
    shapeRegistry.delete(uid);
  }
  for (const shape of incoming) {
    shapeRegistry.set(shape.uid, shape);
  }
}

/** Clear all shapes from registry */
export function clearShapeRegistry(): void {
  shapeRegistry.clear();
}

/** Check if a shape has meaningfully changed */
function hasChanged(prev: TakShape, next: TakShape): boolean {
  return prev.createdAt !== next.createdAt
    || prev.strokeColor !== next.strokeColor
    || prev.fillColor !== next.fillColor;
}
